import { useEffect, useState } from 'react';
import { Globe, Laptop, Loader2, Monitor, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  formatSessionLastAccess,
  type AccountSessionView,
} from '@/lib/painel-account-settings';
import { painelButtonOutlineClass } from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import {
  fetchAccountSessions,
  revokeOtherAccountSessions,
  type AccountSessionDto,
} from '@/services/account-sessions.service';
import { AccountSettingsCard } from './account-settings-shared';

type SessionsCardProps = {
  delay?: number;
};

function mapSession(dto: AccountSessionDto): AccountSessionView {
  return {
    id: String(dto.id),
    os: dto.os,
    browser: dto.browser,
    location: dto.location || dto.ip || '—',
    lastAccessLabel: formatSessionLastAccess(dto.last_used_at),
    isCurrent: dto.is_current,
  };
}

export function SessionsCard({ delay = 0 }: SessionsCardProps) {
  const [sessions, setSessions] = useState<AccountSessionView[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEnding, setIsEnding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rows = await fetchAccountSessions();
        if (!cancelled) setSessions(rows.map(mapSession));
      } catch {
        if (!cancelled) {
          setSessions([]);
          toast.error('Não foi possível carregar as sessões.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleEndOthers = async () => {
    setIsEnding(true);
    try {
      const rows = await revokeOtherAccountSessions();
      setSessions(rows.map(mapSession));
      toast.success('Outras sessões encerradas.');
    } catch {
      toast.error('Não foi possível encerrar as outras sessões.');
    } finally {
      setIsEnding(false);
    }
  };

  const hasOthers = sessions.some((s) => !s.isCurrent);

  return (
    <AccountSettingsCard
      title="Sessões"
      description="Dispositivos conectados à sua conta administrativa."
      icon={Laptop}
      accentClass="bg-indigo-500/15 text-indigo-300 ring-indigo-400/25"
      delay={delay}
    >
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            A carregar sessões…
          </div>
        ) : sessions.length === 0 ? (
          <p className="py-4 font-inter text-sm text-slate-400">
            Nenhuma sessão registada ainda. Faça login novamente para começar a listar dispositivos.
          </p>
        ) : (
          sessions.map((session) => (
            <div
              key={session.id}
              className={cn(
                'flex flex-col gap-4 rounded-xl border border-white/[0.08] bg-white/[0.03] p-5 sm:flex-row sm:items-center sm:justify-between',
                'transition-colors duration-200 hover:border-sky-400/20 hover:bg-white/[0.05]'
              )}
            >
              <div className="flex min-w-0 items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-500/15 ring-1 ring-sky-400/25">
                  <Monitor className="h-5 w-5 text-sky-300" aria-hidden />
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-poppins text-sm font-semibold text-white">{session.os}</p>
                    {session.isCurrent ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 font-inter text-[10px] font-semibold uppercase tracking-wide text-emerald-300 ring-1 ring-emerald-400/25">
                        <Shield className="h-3 w-3" />
                        Sessão atual
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 font-inter text-sm text-slate-400">{session.browser}</p>
                  <p className="mt-2 flex items-center gap-1.5 font-inter text-xs text-slate-500">
                    <Globe className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    {session.location}
                  </p>
                  <p className="mt-1 font-inter text-xs text-slate-500">
                    Último acesso: <span className="text-slate-400">{session.lastAccessLabel}</span>
                  </p>
                </div>
              </div>
            </div>
          ))
        )}

        <Button
          type="button"
          variant="ghost"
          className={cn(painelButtonOutlineClass, 'h-10')}
          onClick={handleEndOthers}
          disabled={isEnding || loading || !hasOthers}
        >
          {isEnding ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Encerrando…
            </>
          ) : (
            'Encerrar outras sessões'
          )}
        </Button>
      </div>
    </AccountSettingsCard>
  );
}
